import useUpdateManualSizeBias from "@babackapp/private-api-client/client/use-update-manual-size-bias";
import { ManualSizeBiasProductItemDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { ColumnDef } from "@tanstack/react-table";
import { Trash2 } from "lucide-react";

type ManualSizeBias = ManualSizeBiasProductItemDto["manualSizeBias"];

function ManualSizeBiasSelect({
  shopId,
  productId,
  manualSizeBias,
}: {
  shopId: string;
  productId: string;
  manualSizeBias: ManualSizeBias;
}) {
  const t = useTranslation();
  const { mutate: updateManualSizeBias } = useUpdateManualSizeBias(
    shopId,
    productId,
  );

  return (
    <div className="flex items-center gap-2 justify-end w-full">
      <Select
        value={manualSizeBias ?? undefined}
        onValueChange={(value) =>
          updateManualSizeBias({
            manualSizeBias: value as Exclude<ManualSizeBias, null>,
          })
        }
      >
        <SelectTrigger className="h-9 w-[200px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="runs_small">
            {t("sizing.manualBias.option.runsSmall")}
          </SelectItem>
          <SelectItem value="true_to_size">
            {t("sizing.manualBias.option.trueToSize")}
          </SelectItem>
          <SelectItem value="runs_large">
            {t("sizing.manualBias.option.runsLarge")}
          </SelectItem>
        </SelectContent>
      </Select>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-9 w-9 text-muted-foreground hover:text-destructive"
        title={t("sizing.manualBias.removeOverride")}
        onClick={() => updateManualSizeBias({ manualSizeBias: null })}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );
}

export function useManualBiasColumns(
  shopId: string,
): ColumnDef<ManualSizeBiasProductItemDto>[] {
  const t = useTranslation();

  return [
    {
      accessorKey: "title",
      header: t("sizing.manualBias.column.product"),
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          {row.original.imageUrl ? (
            <img
              src={row.original.imageUrl}
              alt=""
              className="size-8 rounded object-cover"
            />
          ) : (
            <div className="size-8 rounded bg-muted" />
          )}
          <span className="font-medium">{row.original.title}</span>
        </div>
      ),
    },
    {
      id: "manualSizeBias",
      accessorKey: "manualSizeBias",
      header: t("sizing.manualBias.column.behavior"),
      enableSorting: true,
      cell: ({ row }) => (
        <ManualSizeBiasSelect
          shopId={shopId}
          productId={row.original.productId}
          manualSizeBias={row.original.manualSizeBias}
        />
      ),
    },
  ];
}
